/**
 * BroadCast コンポーネント
 *
 * 配信管理のメイン画面コンポーネント。
 * 通常配信とセグメント配信を切り替えて表示でき、
 * それぞれ専用のテーブルコンポーネントで配信リストを表示する。
 *
 * @file 配信リストメイン画面
 * @date 2025/09/25
 */

import React, { useState } from 'react';
import { Button, Card, Flex, Typography, Radio } from 'antd';
import * as icon from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { BroadCastTable, SegmentBroadCastTable } from 'src/components/model/broadCast/table';

/**
 * 配信タイプの定義
 */
type DeliveryType = 'normal' | 'segment';

/**
 * BroadCast コンポーネント
 *
 * 配信一覧画面のメインコンポーネント。
 * ラジオボタンで通常配信とセグメント配信を切り替え、
 * 対応するテーブルコンポーネントを表示する。
 * 新規作成ボタンから配信作成画面に遷移可能。
 *
 * @returns JSX.Element
 */
export const BroadCast: React.FC = () => {
  const navigate = useNavigate();

  /**
   * 選択された配信タイプの状態管理
   * デフォルトは通常配信を表示
   */
  const [selectedDeliveryType, setSelectedDeliveryType] = useState<DeliveryType>('normal');

  /**
 * 新規作成ボタンクリック時の処理
 * 配信作成画面に遷移（配信タイプは作成画面内で選択）
 */
  const onClickCreate = React.useCallback(() => {
    const path = 'new';
    navigate(path);
  }, [navigate]); 

  return (
    <div style={{ height: '100%' }}>
      <Radio.Group
        value={selectedDeliveryType}
        onChange={(e) => setSelectedDeliveryType(e.target.value)}
        style={{ marginBottom: 16 }}
      >
        <Radio.Button value="normal">通常配信</Radio.Button>
        <Radio.Button value="segment">セグメント配信</Radio.Button>
      </Radio.Group>

      <Card style={{ height: '10%' }}>
        <Flex justify="space-between" align="middle">
          <Typography.Title level={4} style={{ alignSelf: 'center' }}>
            {selectedDeliveryType === 'segment' ? 'セグメント配信リスト' : '配信リスト'}
          </Typography.Title>
          <Button
            type="primary"
            icon={<icon.PlusOutlined />}
            onClick={onClickCreate}
          >
            新規作成
          </Button>
        </Flex>
      </Card>

      <div style={{ height: '15px' }} />

      {selectedDeliveryType === 'normal' ? (
        <BroadCastTable />
      ) : (
        <SegmentBroadCastTable />
      )}
    </div>
  );
};
